import { assign, createMachine, InterpreterFrom, sendParent } from 'xstate'

export type DonationContext = {
  presets: number[]
  presetDonation?: number
  customDonation?: string
  customDonationIsValid?: boolean
  donationIsSet?: boolean
  donation?: number
}

export type DonationState =
  | {
      // no preset or custom donation has been entered
      value: { universal: 'unset' }
      context: DonationContext & {
        customDonation: undefined
        customDonationIsValid: false
        donationIsSet: false
        donation: undefined
      }
    }
  | {
      // preset has been chosen
      value: { universal: 'preset' }
      context: DonationContext & {
        customDonation: undefined
        customDonationIsValid: false
        donationIsSet: true
        donation: number
      }
    }
  | {
      // user is entering custom donation
      value: { universal: 'custom' }
      context: DonationContext & {
        customDonation: string
        donationIsSet: boolean
        donation: number
      }
    }

type SetCustomEvent = {
  type: 'SET_CUSTOM'
  value: string
}

type SaveCustomEvent = {
  type: 'SAVE_CUSTOM'
}

type SavePresetEvent = {
  type: 'SAVE_PRESET'
  amount: number
}

type ResetEvent = {
  type: 'RESET'
}

export type DonationEvent =
  | SetCustomEvent
  | SaveCustomEvent
  | SavePresetEvent
  | ResetEvent

const donationAmountIsValid = (value: string) =>
  value === '' || (value !== undefined && parseFloat(value) >= 0)

const customDonationIsValid = (context: DonationContext) =>
  !!context.customDonation && donationAmountIsValid(context.customDonation)

const presetDonationIsValid = (
  context: DonationContext,
  event: SavePresetEvent
) => context.presets.includes(event.amount)

const setCustomDonation = assign<DonationContext, SetCustomEvent>({
  customDonation: (_context, event) => event.value,
  customDonationIsValid: (_context, event) =>
    donationAmountIsValid(event.value),
})

const saveCustomDonation = assign<DonationContext, SaveCustomEvent>({
  donation: (context) =>
    context.customDonation !== undefined
      ? parseFloat(context.customDonation)
      : undefined,
  donationIsSet: true,
  presetDonation: undefined,
})

const savePresetDonation = assign<DonationContext, SavePresetEvent>({
  donation: (_context, event) => event.amount,
  presetDonation: (_context, event) => event.amount,
  customDonation: undefined,
  donationIsSet: true,
})

const resetDonation = assign<DonationContext, ResetEvent>({
  donation: undefined,
  presetDonation: undefined,
  customDonation: undefined,
  customDonationIsValid: false,
  donationIsSet: false,
})

export const donationMachine = createMachine<
  DonationContext,
  DonationEvent,
  DonationState
>({
  id: 'donation',

  context: {
    presets: [],
    donation: undefined,
  },

  initial: 'universal',

  states: {
    universal: {
      initial: 'unset',

      states: {
        unset: {},
        preset: {},
        custom: {
          entry: 'custom',
        },
      },

      on: {
        SET_CUSTOM: [
          {
            target: '.custom',
            actions: setCustomDonation,
          },
        ],

        SAVE_CUSTOM: [
          {
            cond: customDonationIsValid,
            actions: [
              saveCustomDonation,
              sendParent(({ donation }) => ({
                type: 'SET_DONATION',
                donation,
              })),
            ],
          },
        ],

        SAVE_PRESET: [
          {
            target: '.preset',
            cond: presetDonationIsValid,
            actions: [
              savePresetDonation,
              sendParent((_context, { amount }) => ({
                type: 'SET_DONATION',
                donation: amount,
              })),
            ],
          },
        ],

        RESET: [
          {
            target: '.unset',
            actions: [
              resetDonation,
              sendParent(() => ({ type: 'SET_DONATION', donation: 0 })),
            ],
          },
        ],
      },
    },
  },
  predictableActionArguments: true,
})

export type DonationInterpreter = InterpreterFrom<typeof donationMachine>
